/**
 * 공통 리치 텍스트 에디터 (Tiptap 기반).
 *
 * 사용처:
 *  - 메일 본문 / 메일 서명
 *  - 채널 게시글 작성·수정
 *  - 그 외 HTML 편집이 필요한 모든 화면
 *
 * 디자인:
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │ [폰트▼] [크기▼] │ B I U S │ 글자색▾ 형광펜▾ │ ≡ ≣ ≡ ≡ │ ≣ 1≣ ⟶ ⟵ │ Tx │ ─ ✦ 🔗 ▦ │ 🖼  ─  ↶ ↷ │
 *   └─────────────────────────────────────────────────────────────────┘
 *  단일 가로 행, 풀폭, 그룹 사이 세로 구분선.
 *
 * 기본 폰트: 맑은 고딕 (Malgun Gothic / 맑은 고딕 / Apple SD Gothic Neo).
 * 사용자가 폰트를 바꾸면 그 마크가 인라인 style 로 들어가 메일·게시글에도 적용된다.
 *
 * Props:
 *   value         (controlled)         — HTML 문자열. onChange 와 함께 쓰면 controlled.
 *   initialValue  (uncontrolled)       — 초기 HTML 만 주고, 이후 onChange 만 받음 (호환용).
 *   onChange      (html: string)       — Tiptap getHTML() 동기화
 *   onTextChange  (text: string)       — Tiptap getText() (multipart/alternative 의 plaintext 용)
 *   onEditorReady (editor)             — 외부에서 insertContent / 명령 호출 시
 *   onUploadImage (file) => Promise<url|null>
 *   editorKey                          — 외부에서 강제 reset 트리거 (모달 reopen 등)
 *   placeholder, minHeight
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControlLabel, IconButton, Menu, Popover, Select, MenuItem,
    TextField, Tooltip, Typography,
} from "@mui/material";
import MentionAutocomplete from "./MentionAutocomplete";
import FormatBoldRoundedIcon         from "@mui/icons-material/FormatBoldRounded";
import ActionButton from "./ActionButton";
import FormatItalicRoundedIcon       from "@mui/icons-material/FormatItalicRounded";
import FormatUnderlinedRoundedIcon   from "@mui/icons-material/FormatUnderlinedRounded";
import StrikethroughSRoundedIcon     from "@mui/icons-material/StrikethroughSRounded";
import FormatColorTextRoundedIcon    from "@mui/icons-material/FormatColorTextRounded";
import FormatColorFillRoundedIcon    from "@mui/icons-material/FormatColorFillRounded";
import FormatAlignLeftRoundedIcon    from "@mui/icons-material/FormatAlignLeftRounded";
import FormatAlignCenterRoundedIcon  from "@mui/icons-material/FormatAlignCenterRounded";
import FormatAlignRightRoundedIcon   from "@mui/icons-material/FormatAlignRightRounded";
import FormatAlignJustifyRoundedIcon from "@mui/icons-material/FormatAlignJustifyRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import FormatIndentIncreaseRoundedIcon from "@mui/icons-material/FormatIndentIncreaseRounded";
import FormatIndentDecreaseRoundedIcon from "@mui/icons-material/FormatIndentDecreaseRounded";
import FormatClearRoundedIcon        from "@mui/icons-material/FormatClearRounded";
import LinkRoundedIcon               from "@mui/icons-material/LinkRounded";
import TableRowsRoundedIcon          from "@mui/icons-material/TableRowsRounded";
import HorizontalRuleRoundedIcon     from "@mui/icons-material/HorizontalRuleRounded";
import EmojiSymbolsRoundedIcon       from "@mui/icons-material/EmojiSymbolsRounded";
import AddPhotoAlternateRoundedIcon  from "@mui/icons-material/AddPhotoAlternateRounded";
import UndoRoundedIcon               from "@mui/icons-material/UndoRounded";
import RedoRoundedIcon               from "@mui/icons-material/RedoRounded";

import { useEditor, EditorContent } from "@tiptap/react";
import { Extension, Node } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import StarterKit                    from "@tiptap/starter-kit";
import UnderlineExt                  from "@tiptap/extension-underline";
import { TextStyle as TextStyleExt } from "@tiptap/extension-text-style";
import ColorExt                      from "@tiptap/extension-color";
import FontFamilyExt                 from "@tiptap/extension-font-family";
import { Image as ImageExt }         from "@tiptap/extension-image";
import { Link as LinkExt }           from "@tiptap/extension-link";
import { Table as TableExt, TableRow as TableRowExt, TableCell as TableCellExt, TableHeader as TableHeaderExt } from "@tiptap/extension-table";
import { notify } from "../../util/toast";

// TableCell/TableHeader 의 schema 에 backgroundColor 추가용 attribute 정의.
// addAttributes() 는 Tiptap 이 editor 생성 시점에 호출하므로 closure 로 참조 가능 —
// 다만 가독성을 위해 사용처 위쪽에 둔다. 자세한 의도 설명은 아래 cellBackgroundAttribute 주석 참조.
const cellBackgroundAttribute = {
    backgroundColor: {
        default: null,
        parseHTML: el => {
            const m = /background-color\s*:\s*([^;]+)/i.exec(el.getAttribute("style") || "");
            return m ? m[1].trim() : null;
        },
        renderHTML: attrs => attrs.backgroundColor
            ? { style: `background-color: ${attrs.backgroundColor};` }
            : {},
    },
};

/** TableCell + backgroundColor 속성. */
const CustomTableCell = TableCellExt.extend({
    addAttributes() {
        return { ...this.parent?.(), ...cellBackgroundAttribute };
    },
});
/** TableHeader + backgroundColor 속성. */
const CustomTableHeader = TableHeaderExt.extend({
    addAttributes() {
        return { ...this.parent?.(), ...cellBackgroundAttribute };
    },
});

/**
 * 문서에 서명 블록이 있는가.
 *
 * <p>아래 클릭 보정은 서명이 본문 자리를 잡아먹는 <b>메일 작성기 전용</b> 처리다.
 * RichTextEditor 는 채널·회의록·피드백도 함께 쓰는데, 거기서 "빈 곳 클릭 → 첫 문단"
 * 으로 보내면 긴 글 이어쓰기가 매번 맨 위로 튀어 오히려 방해가 된다.
 * 그래서 서명이 실제로 있는 문서에서만 보정이 켜지게 한다.
 */
function docHasSignature(state) {
    let found = false;
    state.doc.forEach(node => { if (node.type.name === "signatureBlock") found = true; });
    return found;
}

/**
 * 글을 쓸 수 있는 가장 앞 위치(첫 문단)로 커서를 옮기고 포커스를 준다.
 *
 * <p>{@code TextSelection.near(resolve(0), 1)} — 문서 맨 앞에서 <b>뒤쪽으로</b> 가장 가까운
 * 텍스트 위치를 찾는다. 첫 노드가 서명 같은 atom 이면 건너뛰고 그다음 문단을 잡는다.
 */
function focusFirstTextBlock(view) {
    const { state } = view;
    const selection = TextSelection.near(state.doc.resolve(0), 1);
    view.dispatch(state.tr.setSelection(selection).scrollIntoView());
    view.focus();
}

/* ── 기본 폰트 — 맑은 고딕 ───────────────────────────── */
export const MALGUN_GOTHIC = "'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', sans-serif";

/* ── 폰트사이즈 — TextStyle 확장 ─────────────── */
const FontSize = Extension.create({
    name: "fontSize",
    addOptions() { return { types: ["textStyle"] }; },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                fontSize: {
                    default: null,
                    parseHTML: el => el.style.fontSize || null,
                    renderHTML: attrs => attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
                },
            },
        }];
    },
    addCommands() {
        return {
            setFontSize:   size => ({ chain }) => chain().setMark("textStyle", { fontSize: size }).run(),
            unsetFontSize: ()   => ({ chain }) => chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
        };
    },
});

/* ── 텍스트 정렬 — 자체 작성 (extension-text-align 미설치) ─ */
const TextAlign = Extension.create({
    name: "textAlign",
    addOptions() { return { types: ["paragraph", "heading"], alignments: ["left", "center", "right", "justify"] }; },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                textAlign: {
                    default: null,
                    parseHTML: el => el.style.textAlign || null,
                    renderHTML: attrs => attrs.textAlign ? { style: `text-align: ${attrs.textAlign}` } : {},
                },
            },
        }];
    },
    addCommands() {
        return {
            setTextAlign:   alignment => ({ commands }) =>
                this.options.types.every(t => commands.updateAttributes(t, { textAlign: alignment })),
            unsetTextAlign: () => ({ commands }) =>
                this.options.types.every(t => commands.resetAttributes(t, "textAlign")),
        };
    },
});

/* ──────────────────────────────────────────────────────────────────────────
 *  CustomImage — @tiptap/extension-image 에 width/height 속성 추가.
 *
 *  기본 ImageExt 는 src/alt/title 만 schema 에 두고, getHTML() 에 width/height 를 안 실음.
 *  메일 시스템에선 사용자가 이미지를 삽입한 뒤 크기를 조절하고 그 결과를 받는 사람도
 *  같은 비율로 봐야 하므로 schema attribute 로 끌어올린다.
 *
 *  - parseHTML: img 태그의 width 속성 또는 inline style:width(px) 둘 다 인식
 *  - renderHTML: width 가 있으면 width="N" 속성 + style="width:Npx; height:auto;" 동시 부여
 *    (메일 클라이언트별 호환 — Outlook 은 width 속성, Apple Mail/Gmail 은 inline style 우선)
 *  - height 는 비율 유지가 일반적이라 보통 auto. 사용자가 명시적으로 입력했을 때만 저장.
 *  ────────────────────────────────────────────────────────────────────── */
const CustomImage = ImageExt.extend({
    addAttributes() {
        const parent = this.parent?.() || {};
        return {
            ...parent,
            width: {
                default: null,
                parseHTML: el => {
                    const w = el.getAttribute("width");
                    if (w) return parseInt(w, 10);
                    const m = /width\s*:\s*(\d+)\s*px/i.exec(el.getAttribute("style") || "");
                    return m ? parseInt(m[1], 10) : null;
                },
                renderHTML: attrs => {
                    if (!attrs.width) return {};
                    return {
                        width: attrs.width,
                        style: `width: ${attrs.width}px; height: auto; max-width: 100%;`,
                    };
                },
            },
            height: {
                default: null,
                parseHTML: el => {
                    const h = el.getAttribute("height");
                    if (h) return parseInt(h, 10);
                    const m = /height\s*:\s*(\d+)\s*px/i.exec(el.getAttribute("style") || "");
                    return m ? parseInt(m[1], 10) : null;
                },
                renderHTML: attrs => attrs.height ? { height: attrs.height } : {},
            },
        };
    },

    /**
     * NodeView — 마우스 드래그로 이미지 크기 조절.
     *
     * <h4>왜 NodeView 인가</h4>
     * <p>기본 ImageExt 는 단순 {@code <img>} 태그만 렌더 → 마우스 핸들·hover 효과를 붙일 곳이
     * 없음. NodeView 로 wrapper {@code <span>} 안에 img + 우하단 corner handle 을 두고,
     * handle 드래그 이벤트로 width 실시간 갱신.
     *
     * <h4>UX</h4>
     * <ul>
     *   <li>이미지에 마우스 hover → 우하단 파란 코너 핸들 등장</li>
     *   <li>핸들 드래그 → width 실시간 변경 (preview)</li>
     *   <li>마우스 놓을 때 → 노드 attrs.width 에 커밋 (HTML 직렬화에 반영)</li>
     *   <li>더블클릭 → 기존 ImageResizeDialog (정밀 입력) 이 그대로 동작</li>
     * </ul>
     *
     * <h4>작동 원리</h4>
     * <p>NodeView 는 ProseMirror 가 호출하는 함수. dom (wrapper) 와 update (attrs 변경 동기화)
     * 만 노출. 노드의 attrs 가 외부에서 바뀌면 update 가 호출되어 img.width 를 동기화.
     * 우리 setNodeSelection + updateAttributes 가 그 트리거.
     */
    addNodeView() {
        return ({ node, editor, getPos }) => {
            const wrapper = document.createElement("span");
            wrapper.className = "image-wrap";
            wrapper.style.cssText = "display: inline-block; position: relative; line-height: 0; max-width: 100%;";

            const img = document.createElement("img");
            img.src = node.attrs.src;
            if (node.attrs.alt) img.alt = node.attrs.alt;
            if (node.attrs.title) img.title = node.attrs.title;
            const applyWidth = (w) => {
                if (w) {
                    img.style.width  = `${w}px`;
                    img.style.height = "auto";
                    img.setAttribute("width", String(w));
                } else {
                    img.style.removeProperty("width");
                    img.style.removeProperty("height");
                    img.removeAttribute("width");
                }
            };
            img.style.maxWidth = "100%";
            img.style.display  = "block";
            img.style.borderRadius = "4px";
            img.style.cursor   = "default";
            applyWidth(node.attrs.width);
            wrapper.appendChild(img);

            // 우하단 corner 리사이즈 핸들 — hover 시에만 보이고, mousedown → 드래그
            const handle = document.createElement("span");
            handle.className = "image-resize-handle";
            handle.style.cssText = `
                position: absolute;
                right: -5px;
                bottom: -5px;
                width: 12px;
                height: 12px;
                background: #2563eb;
                border: 2px solid #fff;
                border-radius: 50%;
                cursor: nwse-resize;
                opacity: 0;
                transition: opacity 0.1s;
                z-index: 10;
                box-shadow: 0 0 0 1px rgba(0,0,0,0.1);
            `;
            wrapper.appendChild(handle);

            wrapper.addEventListener("mouseenter", () => { handle.style.opacity = "1"; });
            wrapper.addEventListener("mouseleave", () => { handle.style.opacity = "0"; });

            // 드래그 리사이즈
            let dragState = null;
            handle.addEventListener("mousedown", (e) => {
                e.preventDefault();
                e.stopPropagation();
                dragState = {
                    startX: e.clientX,
                    startWidth: img.offsetWidth || node.attrs.width || 200,
                };
                document.addEventListener("mousemove", onMouseMove);
                document.addEventListener("mouseup", onMouseUp);
            });

            function onMouseMove(ev) {
                if (!dragState) return;
                const delta = ev.clientX - dragState.startX;
                const newW = Math.max(50, Math.min(1500, dragState.startWidth + delta));
                // preview only — 실시간 시각 변경
                img.style.width = `${newW}px`;
                img.style.height = "auto";
            }

            function onMouseUp() {
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
                if (!dragState) return;

                const finalW = img.offsetWidth;
                dragState = null;

                // ProseMirror 노드 attrs 에 커밋 — 직렬화 시 반영
                if (typeof getPos === "function") {
                    try {
                        editor.chain()
                            .setNodeSelection(getPos())
                            .updateAttributes("image", { width: finalW })
                            .run();
                    } catch { /* selection 실패 시 무시 — 다음 트리거에서 정상화 */ }
                }
            }

            return {
                dom: wrapper,
                /**
                 * 외부에서 노드 attrs 가 바뀌면 호출됨 — 다이얼로그로 width 변경 시, src 변경 시 등.
                 * 같은 image 노드라면 dom 재생성 없이 update 만 (효율).
                 */
                update(updatedNode) {
                    if (updatedNode.type.name !== "image") return false;
                    if (updatedNode.attrs.src !== img.src) img.src = updatedNode.attrs.src;
                    applyWidth(updatedNode.attrs.width);
                    return true;
                },
                destroy() {
                    document.removeEventListener("mousemove", onMouseMove);
                    document.removeEventListener("mouseup", onMouseUp);
                },
            };
        };
    },
});

/* ── 형광펜 — TextStyle 의 backgroundColor 마크 ─ */
const Highlight = Extension.create({
    name: "highlight",
    addOptions() { return { types: ["textStyle"] }; },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                backgroundColor: {
                    default: null,
                    parseHTML: el => el.style.backgroundColor || null,
                    renderHTML: attrs => attrs.backgroundColor ? { style: `background-color: ${attrs.backgroundColor}` } : {},
                },
            },
        }];
    },
    addCommands() {
        return {
            setHighlight:   c  => ({ chain }) => chain().setMark("textStyle", { backgroundColor: c }).run(),
            unsetHighlight: () => ({ chain }) => chain().setMark("textStyle", { backgroundColor: null }).removeEmptyTextStyle().run(),
        };
    },
});

/* ──────────────────────────────────────────────────────────────────────────
 *  SignatureBlock — 서명 영역을 원본 그대로 보존하는 atom 노드.
 *
 *  Tiptap StarterKit 은 일반 <div> 를 모름 → insertContent("<div data-signature>...</div>")
 *  하면 div 를 unwrap 하고 안의 자식들만 살림. 그래서 우리 marker 가 사라짐.
 *  그래서 "div[data-signature]" 를 인식하는 전용 노드를 둔다.
 *
 *  <h4>왜 content:"block+" 가 아니라 atom 인가</h4>
 *  이전엔 자식을 block+ 로 두어 서명 내부까지 ProseMirror 스키마로 파싱했다. 그런데
 *  스키마는 자기가 아는 속성만 남기고 나머지는 버린다 — 특히 TableCell 확장이
 *  style 을 스키마 속성으로 갖고 있지 않아 <td style="..."> 가 통째로 사라졌다.
 *  타사 메일에서 복사해 온 명함형 서명은 바로 그 td 인라인 style 로 모양을 잡으므로
 *  (빨간 밑줄 = border-bottom, 2단 배치 = width/valign) 붙여넣는 순간 디자인이 부서졌다.
 *  실측: 2464자 서명 → 1445자, 빨간 밑줄·표 폭·셀 패딩 전부 소실.
 *
 *  atom 으로 두면 ProseMirror 가 내부를 아예 파싱하지 않고 html 속성 문자열로만 들고 있다가
 *  그대로 다시 뱉는다. 같은 서명 실측 2464자 → 2504자(래퍼 div 만큼만 증가, 무손실).
 *
 *  <h4>대가 — 작성창에서 서명 내부는 편집 불가</h4>
 *  atom 은 leaf 라 커서가 안으로 들어가지 못한다. 서명 블록 자체를 선택해 지우거나
 *  앞뒤에 쓰는 건 되지만, 서명 안 전화번호를 이 자리에서 고치는 건 안 된다.
 *  서명 수정은 "서명 설정" 에서 한다 — 서명은 매 메일 손보는 것이 아니라 한 번 만들어
 *  계속 쓰는 것이므로 무손실 보존을 택했다.
 *
 *  <h4>주변 코드와의 계약</h4>
 *  렌더 결과는 여전히 <div data-signature="1" class="signature-block"> 래퍼다.
 *  - aiBodyRevise.extractBlocks 가 el.closest("[data-signature]") 로 AI 검수에서 제외
 *  - ComposeModal.normalizeBodyForPreview 가 같은 셀렉터로 표 보더 강제에서 제외
 *  - 위 표 CSS 규칙도 [data-signature] 안에서는 격자선을 그리지 않음
 *  이 셀렉터를 바꾸면 세 곳이 같이 깨진다.
 *
 *  ProseMirror 는 content hole 이 없는 leaf 노드의 DOM 에 contentEditable=false 를
 *  런타임에 직접 얹는다. 이건 live view 에만 적용되고 getHTML() 은 renderHTML 로
 *  새로 직렬화하므로, 발송 본문에 contenteditable 이 섞여 나가지 않는다.
 *  ────────────────────────────────────────────────────────────────────── */
const SignatureBlock = Node.create({
    name: "signatureBlock",
    group: "block",
    atom: true,
    selectable: true,
    addAttributes() {
        return {
            /** 서명 HTML 원문. 삽입 전에 DOMPurify 를 통과한 것만 들어온다(ComposeModal.prepareSignatureHtml). */
            html: {
                default: "",
                parseHTML: el => el.innerHTML,
                renderHTML: () => ({}),   // DOM 속성으로 새어나가면 안 됨 — 아래에서 innerHTML 로 직접 렌더
            },
        };
    },
    parseHTML() {
        return [{ tag: "div[data-signature]" }];
    },
    renderHTML({ node }) {
        const el = document.createElement("div");
        el.setAttribute("data-signature", "1");
        el.setAttribute("class", "signature-block");
        el.innerHTML = node.attrs.html || "";
        return el;
    },
});

const FONT_FAMILIES = [
    { label: "맑은 고딕",   value: MALGUN_GOTHIC },
    { label: "Pretendard",  value: "Pretendard, sans-serif" },
    { label: "나눔고딕",    value: "'Nanum Gothic', sans-serif" },
    { label: "고딕체",      value: "'Noto Sans KR', sans-serif" },
    { label: "명조체",      value: "'Noto Serif KR', serif" },
    { label: "고정폭",      value: "monospace" },
];
const FONT_SIZES = ["10px","12px","14px","16px","18px","20px","24px","28px","32px"];

const TEXT_COLORS = [
    "#0f172a", "#dc2626", "#ea580c", "#d97706", "#16a34a", "#0d9488",
    "#0284c7", "#2563eb", "#7c3aed", "#c026d3", "#db2777", "#475569",
    "#64748b", "#94a3b8", "#cbd5e1", "#ffffff",
];
const HIGHLIGHT_COLORS = [
    "transparent", "#fef9c3", "#fed7aa", "#fecaca", "#fbcfe8",
    "#e9d5ff", "#bfdbfe", "#bbf7d0", "#fde68a", "#cffafe",
];
const SPECIAL_CHARS = ["•","·","※","★","☆","✓","✗","→","←","↑","↓","↔","∴","≒","≠","±","°","℃","℉","€","₩","§","†","‡","®","™","©","♥","♦","♣","♠"];

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error("파일을 읽지 못했습니다."));
        reader.readAsDataURL(file);
    });
}

/* ── 툴바 버튼 ─────────────── */
function ToolBtn({ active, onClick, children, title, disabled }) {
    return (
        <Tooltip title={title || ""} placement="top">
            <span>
                <IconButton size="small" disabled={disabled}
                    onMouseDown={e => { e.preventDefault(); onClick?.(); }}
                    sx={{
                        p: 0.5, borderRadius: 1,
                        color: active ? "#2563eb" : "#475569",
                        bgcolor: active ? "#dbeafe" : "transparent",
                        "&:hover": { bgcolor: active ? "#dbeafe" : "#f1f5f9" },
                    }}>
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
}

/* ── 색상 팝업 ──── */
function ColorPopover({ anchorEl, open, onClose, palette, current, onPick, onClear, clearLabel }) {
    return (
        <Popover open={open} anchorEl={anchorEl} onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ sx: { p: 1, borderRadius: 1.5 } }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(8, 22px)", gap: 0.6 }}>
                {palette.map(c => (
                    <Box key={c} onMouseDown={(e) => { e.preventDefault(); onPick?.(c); onClose(); }}
                        sx={{
                            width: 22, height: 22, borderRadius: 0.8, cursor: "pointer",
                            bgcolor: c === "transparent" ? "#fff" : c,
                            backgroundImage: c === "transparent"
                                ? "linear-gradient(135deg, transparent 47%, #ef4444 47%, #ef4444 53%, transparent 53%)"
                                : "none",
                            border: current === c ? "2px solid #2563eb" : "1px solid #e2e8f0",
                            "&:hover": { transform: "scale(1.15)" },
                            transition: "transform 0.1s",
                        }} />
                ))}
            </Box>
            <Box
                onMouseDown={(e) => { e.preventDefault(); onClear?.(); onClose(); }}
                sx={{
                    mt: 1, px: 1, py: 0.5, borderRadius: 1,
                    fontSize: 12, color: "#64748b", textAlign: "center",
                    cursor: "pointer", border: "1px solid #e2e8f0",
                    "&:hover": { bgcolor: "#f1f5f9" },
                }}>
                {clearLabel || "지우기"}
            </Box>
        </Popover>
    );
}

export default function RichTextEditor({
    value,
    initialValue,
    onChange,
    onTextChange,
    placeholder = "내용을 입력하세요...",
    minHeight = 240,
    editorKey,
    onUploadImage,
    onEditorReady,
    // 외곽 border 제거 — 부모가 더 큰 통합 컨테이너 안에서 다른 영역과 한 흐름으로 통합 표시할 때 사용.
    noOuterBorder = false,
    // @ 멘션 자동완성용 멤버 목록. 비어있거나 없으면 멘션 기능 자체가 비활성 (메일 등 다른 사용처는 무영향).
    // 원소 형태: { id, name, deptName, positionName } 또는 { employee: { ... } } 둘 다 허용.
    mentionMembers = [],
    // 비동기 멘션 모드 — 메일 본문처럼 채널 멤버가 아니라 API 로 사원을 가져올 때 사용.
    // fetcher(query) => Promise<EmployeeSuggestDto[]>. mentionMembers 와 둘 중 하나만 주면 된다.
    mentionFetcher,
    // 멘션 선택 시 콜백 — 외부에서 후속 처리(예: 메일 보낼 때 멘션 알람 발송 준비) 필요할 때.
    onMentionPicked,
    /**
     * 드롭된 이미지를 본문에 인라인 삽입하지 않는다 — 부모가 드롭을 직접 처리할 때 켠다.
     *
     * <p>메일 작성 모달이 그 경우다. 작성 영역 전체가 첨부 드롭존이라, 여기서도 이미지를 삽입하면
     * 같은 파일이 <b>인라인 + 첨부 두 벌</b>로 들어간다(ProseMirror 의 handleDrop 은
     * stopPropagation 을 하지 않으므로 부모의 onDrop 도 함께 실행된다).
     * 인라인 이미지는 붙여넣기(handlePaste)로 여전히 가능하다.
     */
    disableImageDrop = false,
}) {
    const [colorAnchor, setColorAnchor] = useState(null);
    const [hlAnchor, setHlAnchor]       = useState(null);
    const [specAnchor, setSpecAnchor]   = useState(null);
    const [tableAnchor, setTableAnchor] = useState(null);
    // 이미지 리사이즈 다이얼로그 상태 — 클릭한 image 노드의 attrs + 작업 중 width/height/lockRatio
    const [imgResize, setImgResize] = useState(null); // null 이면 닫힘. { src, naturalW, naturalH, width, height, lockRatio }
    const imageInputRef = useRef();

    // 클립보드/드롭 핸들러는 editor 생성 시점에 한 번만 클로저에 캡처되므로
    // onUploadImage 가 부모에서 갱신돼도 stale 클로저가 안 되도록 ref 로 우회.
    const uploadImageRef = useRef(onUploadImage);
    useEffect(() => { uploadImageRef.current = onUploadImage; }, [onUploadImage]);

    // 같은 이유(생성 시점 클로저 캡처)로 ref 경유 — prop 이 바뀌어도 핸들러가 최신 값을 본다.
    const disableImageDropRef = useRef(disableImageDrop);
    useEffect(() => { disableImageDropRef.current = disableImageDrop; }, [disableImageDrop]);

    /**
     * 클립보드/드롭으로 들어온 이미지 파일들을 에디터에 삽입.
     * - 업로드 핸들러 우선 사용 (반환된 URL 로 삽입)
     * - 실패 시 base64 data URL 로 폴백 — 발송 단계에서 백엔드가 cid: 로 변환해 multipart/related 임베드.
     */
    const insertImageFiles = async (view, files) => {
        for (const file of files) {
            try {
                let src = null;
                const upload = uploadImageRef.current;
                if (upload) src = await upload(file);
                if (!src) src = await readFileAsDataUrl(file);
                if (!src) continue;
                const node = view.state.schema.nodes.image?.create({ src });
                if (!node) continue;
                view.dispatch(view.state.tr.replaceSelectionWith(node).scrollIntoView());
            } catch (err) {
                console.error("이미지 삽입 실패", err);
                notify(`${file.name} 이미지 처리 실패`, "error");
            }
        }
    };

    // value 가 controlled, initialValue 가 uncontrolled — 우선순위: value > initialValue
    const startContent = (value !== undefined && value !== null) ? value : (initialValue || "");

    const editor = useEditor({
        extensions: [
            StarterKit,
            UnderlineExt,
            TextStyleExt,
            ColorExt,
            FontFamilyExt,
            FontSize,
            Highlight,
            SignatureBlock,
            TextAlign,
            CustomImage.configure({ inline: false, allowBase64: true }),
            LinkExt.configure({
                openOnClick: false, autolink: true,
                HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
            }),
            TableExt.configure({ resizable: true, HTMLAttributes: { class: "tiptap-table" } }),
            TableRowExt,
            CustomTableCell,
            CustomTableHeader,
        ],
        content: startContent,
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
            onTextChange?.(editor.getText());
        },
        editorProps: {
            attributes: { class: "rich-editor-content" },
            /**
             * 서명이 본문 자리를 막는 문제 보정 — 쓸 수 없는 곳을 누르면 첫 문단으로 커서를 보낸다.
             *
             * <h4>왜 필요한가</h4>
             * 서명은 atom(leaf) 이라 커서가 안으로 들어가지 못한다. 그런데 명함형 서명은 세로로
             * 꽤 길어서 작성 영역의 대부분을 차지하고, 그 아래 남는 여백에도 문단이 없다
             * (문서 구조가 [빈 문단][서명] 이라 서명 뒤에는 아무 노드도 없다).
             * 그래서 사용자가 "여기 쓰면 되겠지" 하고 누르는 자리 대부분이 글을 쓸 수 없는 곳이고,
             * 눌러도 커서가 안 생겨 본문 클릭이 안 되는 것처럼 느껴진다.
             *
             * <h4>어떤 클릭을 보정하나</h4>
             * <ol>
             *   <li>서명 안 — 커서가 절대 못 들어가는 자리</li>
             *   <li>문단이 아닌 자리(서명 아래 여백 등) — 단, 그 자리에 이미지 같은 다른 노드가
             *       있으면 건드리지 않는다. 이미지는 단일 클릭 선택 → 더블클릭 크기조절 흐름이 있어서
             *       가로채면 그 기능이 죽는다.</li>
             * </ol>
             * 문단 안을 눌렀을 때는 당연히 기본 동작 그대로다.
             *
             * <h4>서명을 지우려면</h4>
             * 클릭으로는 서명 노드가 선택되지 않으니, 첫 문단 끝에서 Delete(또는 서명 뒤에서
             * Backspace)로 지운다. 서명 자체를 고치는 건 "서명 설정" 에서 한다.
             *
             * 반환 true → ProseMirror 기본 클릭 처리 차단 (우리가 커서를 놓음).
             */
            handleClick: (view, pos, event) => {
                if (!docHasSignature(view.state)) return false;   // 메일 작성기가 아닌 경우 — 기본 동작

                if (event.target?.closest?.("[data-signature]")) {
                    focusFirstTextBlock(view);
                    return true;
                }
                const $pos = view.state.doc.resolve(pos);
                if ($pos.parent.isTextblock) return false;        // 글 쓸 수 있는 자리 — 기본 동작
                const nodeHere = view.state.doc.nodeAt(pos);
                if (nodeHere && nodeHere.type.name !== "signatureBlock") return false; // 이미지 등은 그대로
                focusFirstTextBlock(view);
                return true;
            },
            /**
             * 클립보드 paste 시 이미지 파일이 있으면 직접 처리.
             * Tiptap/ProseMirror 기본 paste 핸들러는 clipboardData.items 의 image/* 파일을
             * 그냥 무시하므로(텍스트/HTML 만 처리) 스크린샷 도구·이미지 파일 복사 등이 누락된다.
             * 여기서 image 항목을 추출해 setImage 노드로 삽입한다.
             *
             * 반환 true → ProseMirror 기본 paste 차단 (우리가 처리 완료).
             * 반환 false → 일반 텍스트/HTML paste 흐름 그대로 진행 (이미지 없는 경우).
             */
            handlePaste: (view, event) => {
                const items = Array.from(event.clipboardData?.items || []);
                const files = items
                    .filter(it => it.kind === "file" && (it.type || "").startsWith("image/"))
                    .map(it => it.getAsFile())
                    .filter(Boolean);
                if (files.length === 0) return false;
                event.preventDefault();
                insertImageFiles(view, files);
                return true;
            },
            /**
             * 드래그-앤-드롭으로 들어온 이미지 파일도 동일 처리.
             * 외부 url 드래그(text/uri-list)는 ProseMirror 기본 흐름에 위임.
             */
            handleDrop: (view, event) => {
                // 부모가 드롭을 직접 처리하는 화면(메일 작성)에서는 여기서 손대지 않는다 —
                // 안 그러면 같은 파일이 인라인 + 첨부 두 벌로 들어간다.
                if (disableImageDropRef.current) return false;
                const dt = event.dataTransfer;
                if (!dt) return false;
                const files = Array.from(dt.files || []).filter(f => (f.type || "").startsWith("image/"));
                if (files.length === 0) return false;
                event.preventDefault();
                insertImageFiles(view, files);
                return true;
            },
            /**
             * 이미지 더블클릭 → 크기 조절 다이얼로그 오픈.
             * - 단일 클릭은 ProseMirror 가 NodeSelection 으로 잡아주는 기본 동작 유지
             * - 더블클릭만 우리 다이얼로그 트리거 — UX 마찰 최소화
             * - HTMLImageElement 의 naturalWidth/Height 로 원본 크기를 알아내 비율 유지의 기준값으로 사용
             */
            handleDoubleClickOn: (view, pos, node, _nodePos, event) => {
                if (node.type.name !== "image") return false;
                const target = event.target;
                const naturalW = target?.naturalWidth || node.attrs.width || 0;
                const naturalH = target?.naturalHeight || node.attrs.height || 0;
                const curW = node.attrs.width || naturalW || 0;
                const curH = node.attrs.height || (naturalW && naturalH ? Math.round(naturalH * (curW / naturalW)) : 0);
                setImgResize({
                    pos: _nodePos,
                    src: node.attrs.src,
                    naturalW,
                    naturalH,
                    width: curW || 600,
                    height: curH || 0,
                    lockRatio: true,
                });
                return true;
            },
        },
    }, [editorKey]);

    useEffect(() => {
        if (editor && onEditorReady) onEditorReady(editor);
    }, [editor, onEditorReady]);

    const prevKeyRef = useRef(editorKey);
    useEffect(() => {
        if (editor && prevKeyRef.current !== editorKey) {
            // Tiptap v3: setContent(content, options). 과거 boolean(false) 은 무시되어 emitUpdate 가
            // 기본 true 로 동작 → 불필요한 onUpdate 1회 유발. 옵션 객체로 명시.
            editor.commands.setContent(startContent || "", { emitUpdate: false });
            prevKeyRef.current = editorKey;
        }
        // eslint-disable-next-line
    }, [editorKey, editor]);

    // @ 멘션 자동완성은 별도 컴포넌트({@link MentionAutocomplete}) 로 분리 — 채널/메일 본문 양쪽에서
    // 같은 UX 와 부수효과(onMentionPicked) 를 갖도록 통일. mentionMembers / mentionFetcher 중 하나만 주면 활성.

    const isAlignActive = useMemo(() => (val) => {
        if (!editor) return false;
        return editor.isActive({ textAlign: val });
    }, [editor]);

    if (!editor) return null;

    // 폰트 마크가 없으면 시각적 기본값 = 맑은 고딕 → Select 도 자동으로 "맑은 고딕" 표시
    const curFont  = editor.getAttributes("textStyle").fontFamily ?? MALGUN_GOTHIC;
    const curSize  = editor.getAttributes("textStyle").fontSize   ?? "14px";
    const curColor = editor.getAttributes("textStyle").color      ?? "#0f172a";
    const curHL    = editor.getAttributes("textStyle").backgroundColor ?? null;

    const handleInsertLink = () => {
        const prev = editor.getAttributes("link").href || "";
        const url  = window.prompt("링크 URL", prev);
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        editor.chain().focus().extendMarkRange("link").setLink({ href: safe }).run();
    };

    const handleInsertTable = (rows, cols) => {
        const r = Math.max(1, Math.min(rows ?? 3, 20));
        const c = Math.max(1, Math.min(cols ?? 3, 20));
        editor.chain().focus().insertTable({ rows: r, cols: c, withHeaderRow: true }).run();
    };

    /**
     * 표 편집 액션 — Tiptap @tiptap/extension-table 의 commands 매핑.
     * 표 안에 caret 이 있을 때만(=isActive("table")) 활성.
     *
     *   addRowBefore / addRowAfter / deleteRow             — 행 추가/삭제
     *   addColumnBefore / addColumnAfter / deleteColumn    — 열 추가/삭제
     *   toggleHeaderRow / toggleHeaderColumn               — 헤더 토글
     *   mergeCells / splitCell                             — 병합/분리 (선택 영역 기반)
     *   deleteTable                                        — 표 통째로 제거
     */
    const inTable = editor.isActive("table");
    const tableEditActions = [
        { key: "addRowAbove",  label: "위에 행 추가",   run: () => editor.chain().focus().addRowBefore().run() },
        { key: "addRowBelow",  label: "아래에 행 추가", run: () => editor.chain().focus().addRowAfter().run() },
        { key: "deleteRow",    label: "행 삭제",         run: () => editor.chain().focus().deleteRow().run(), danger: true },
        { divider: true, key: "div2" },
        { key: "addColLeft",   label: "왼쪽에 열 추가",  run: () => editor.chain().focus().addColumnBefore().run() },
        { key: "addColRight",  label: "오른쪽에 열 추가", run: () => editor.chain().focus().addColumnAfter().run() },
        { key: "deleteCol",    label: "열 삭제",          run: () => editor.chain().focus().deleteColumn().run(), danger: true },
        { divider: true, key: "div3" },
        { key: "toggleHeaderRow",    label: "헤더 행 토글",  run: () => editor.chain().focus().toggleHeaderRow().run() },
        { key: "toggleHeaderColumn", label: "헤더 열 토글",  run: () => editor.chain().focus().toggleHeaderColumn().run() },
        { divider: true, key: "div4" },
        { key: "mergeCells",   label: "셀 병합 (선택 영역)", run: () => editor.chain().focus().mergeCells().run() },
        { key: "splitCell",    label: "셀 분리",              run: () => editor.chain().focus().splitCell().run() },
        { divider: true, key: "div5" },
        { key: "deleteTable",  label: "표 삭제",              run: () => editor.chain().focus().deleteTable().run(), danger: true },
    ];

    const handleIndent = () => {
        if (editor.can().sinkListItem("listItem")) {
            editor.chain().focus().sinkListItem("listItem").run();
        } else {
            editor.chain().focus().insertContent("    ").run();
        }
    };
    const handleOutdent = () => {
        if (editor.can().liftListItem("listItem")) {
            editor.chain().focus().liftListItem("listItem").run();
        }
    };
    const handleClearFormat = () => {
        editor.chain().focus().unsetAllMarks().clearNodes().run();
    };

    const Sep = () => <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5, borderColor: "#e2e8f0" }} />;

    return (
        <Box sx={{
            ...(noOuterBorder
                ? {}
                : {
                    border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden",
                    "&:focus-within": { borderColor: "#2563eb", boxShadow: "0 0 0 2px rgba(37,99,235,0.12)" },
                    transition: "border-color 0.15s, box-shadow 0.15s",
                }),
            display: "flex", flexDirection: "column", flex: 1, minHeight: 0,
            bgcolor: "#fff",
        }}>
            {/* ── 단일 가로 툴바 ───────────────────────────────
                position: sticky — 부모(또는 외곽 wrapper) 가 스크롤될 때도 툴바가 상단에 고정.
                본문이 길어 스크롤 압박이 와도 서식 버튼을 항상 손에 닿게 한다. */}
            <Box sx={{
                position: "sticky", top: 0, zIndex: 2,
                display: "flex", alignItems: "center", flexWrap: "nowrap", gap: 0.2,
                px: 1, py: 0.6, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc",
                flexShrink: 0, overflowX: "auto",
                "&::-webkit-scrollbar": { height: 4 },
                "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: 1.5 },
            }}>
                {/* 폰트 / 크기 */}
                <Select size="small" value={curFont}
                    onChange={e => {
                        if (e.target.value) editor.chain().focus().setFontFamily(e.target.value).run();
                        else editor.chain().focus().unsetFontFamily().run();
                    }}
                    sx={{
                        fontSize: 12.5, height: 30, minWidth: 110,
                        "& .MuiSelect-select": { py: "4px" },
                        "& fieldset": { borderColor: "transparent" },
                        bgcolor: "#fff", borderRadius: 1,
                        "&:hover fieldset": { borderColor: "#cbd5e1" },
                    }}>
                    {FONT_FAMILIES.map(f => <MenuItem key={f.value || "_none"} value={f.value} sx={{ fontSize: 12.5, fontFamily: f.value || undefined }}>{f.label}</MenuItem>)}
                </Select>
                <Select size="small" value={curSize}
                    onChange={e => editor.chain().focus().setFontSize(e.target.value).run()}
                    sx={{
                        fontSize: 12.5, height: 30, minWidth: 70, ml: 0.4,
                        "& .MuiSelect-select": { py: "4px" },
                        "& fieldset": { borderColor: "transparent" },
                        bgcolor: "#fff", borderRadius: 1,
                        "&:hover fieldset": { borderColor: "#cbd5e1" },
                    }}>
                    {FONT_SIZES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 12.5 }}>{s}</MenuItem>)}
                </Select>

                <Sep />

                {/* B I U S */}
                <ToolBtn title="굵게 (Ctrl+B)"  active={editor.isActive("bold")}     onClick={() => editor.chain().focus().toggleBold().run()}><FormatBoldRoundedIcon       sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="기울임 (Ctrl+I)" active={editor.isActive("italic")}   onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalicRoundedIcon     sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="밑줄 (Ctrl+U)"  active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlinedRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="취소선"          active={editor.isActive("strike")}    onClick={() => editor.chain().focus().toggleStrike().run()}><StrikethroughSRoundedIcon  sx={{ fontSize: 17 }} /></ToolBtn>

                {/* 글자색 */}
                <Tooltip title="글자색" placement="top">
                    <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); setColorAnchor(e.currentTarget); }}
                        sx={{ p: 0.5, borderRadius: 1, "&:hover": { bgcolor: "#f1f5f9" } }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <FormatColorTextRoundedIcon sx={{ fontSize: 17, color: curColor }} />
                            <Box sx={{ width: 14, height: 3, mt: "-2px", borderRadius: 0.5, bgcolor: curColor }} />
                        </Box>
                    </IconButton>
                </Tooltip>
                <ColorPopover
                    anchorEl={colorAnchor} open={Boolean(colorAnchor)} onClose={() => setColorAnchor(null)}
                    palette={TEXT_COLORS} current={curColor}
                    onPick={(c) => editor.chain().focus().setColor(c).run()}
                    onClear={() => editor.chain().focus().unsetColor().run()}
                    clearLabel="기본 색상"
                />

                {/* 형광펜 */}
                <Tooltip title="형광펜" placement="top">
                    <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); setHlAnchor(e.currentTarget); }}
                        sx={{ p: 0.5, borderRadius: 1, "&:hover": { bgcolor: "#f1f5f9" } }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <FormatColorFillRoundedIcon sx={{ fontSize: 17, color: "#475569" }} />
                            <Box sx={{ width: 14, height: 3, mt: "-2px", borderRadius: 0.5, bgcolor: curHL || "#fef08a" }} />
                        </Box>
                    </IconButton>
                </Tooltip>
                <ColorPopover
                    anchorEl={hlAnchor} open={Boolean(hlAnchor)} onClose={() => setHlAnchor(null)}
                    palette={HIGHLIGHT_COLORS} current={curHL}
                    onPick={(c) => {
                        if (c === "transparent") editor.chain().focus().unsetHighlight().run();
                        else editor.chain().focus().setHighlight(c).run();
                    }}
                    onClear={() => editor.chain().focus().unsetHighlight().run()}
                    clearLabel="형광펜 지우기"
                />

                <Sep />

                {/* 정렬 */}
                <ToolBtn title="왼쪽 정렬"   active={isAlignActive("left")}    onClick={() => editor.chain().focus().setTextAlign("left").run()}><FormatAlignLeftRoundedIcon    sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="가운데 정렬" active={isAlignActive("center")}  onClick={() => editor.chain().focus().setTextAlign("center").run()}><FormatAlignCenterRoundedIcon  sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="오른쪽 정렬" active={isAlignActive("right")}   onClick={() => editor.chain().focus().setTextAlign("right").run()}><FormatAlignRightRoundedIcon   sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="양쪽 정렬"   active={isAlignActive("justify")} onClick={() => editor.chain().focus().setTextAlign("justify").run()}><FormatAlignJustifyRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>

                <Sep />

                {/* 리스트 / 들여쓰기 */}
                <ToolBtn title="글머리 기호" active={editor.isActive("bulletList")}  onClick={() => editor.chain().focus().toggleBulletList().run()}><FormatListBulletedRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="번호 목록"   active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><FormatListNumberedRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="내어쓰기"    onClick={handleOutdent}><FormatIndentDecreaseRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="들여쓰기"    onClick={handleIndent} ><FormatIndentIncreaseRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>

                <Sep />

                <ToolBtn title="서식 지우기" onClick={handleClearFormat}><FormatClearRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>

                <Sep />

                <ToolBtn title="구분선"     onClick={() => editor.chain().focus().setHorizontalRule().run()}><HorizontalRuleRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>
                <Tooltip title="특수문자" placement="top">
                    <IconButton size="small"
                        onMouseDown={(e) => { e.preventDefault(); setSpecAnchor(e.currentTarget); }}
                        sx={{ p: 0.5, borderRadius: 1, color: "#475569", "&:hover": { bgcolor: "#f1f5f9" } }}>
                        <EmojiSymbolsRoundedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
                <Menu open={Boolean(specAnchor)} anchorEl={specAnchor} onClose={() => setSpecAnchor(null)}
                    PaperProps={{ sx: { p: 1, borderRadius: 1.5 } }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(8, 26px)", gap: 0.4 }}>
                        {SPECIAL_CHARS.map(ch => (
                            <Box key={ch}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    editor.chain().focus().insertContent(ch).run();
                                    setSpecAnchor(null);
                                }}
                                sx={{
                                    width: 26, height: 26, display: "flex", alignItems: "center",
                                    justifyContent: "center", fontSize: 14, cursor: "pointer",
                                    borderRadius: 0.8,
                                    "&:hover": { bgcolor: "#f1f5f9" },
                                }}>
                                {ch}
                            </Box>
                        ))}
                    </Box>
                </Menu>
                <ToolBtn title="링크" active={editor.isActive("link")} onClick={handleInsertLink}><LinkRoundedIcon       sx={{ fontSize: 17 }} /></ToolBtn>
                <Tooltip title={inTable ? "표 편집" : "표 삽입 / 편집"} placement="top">
                    <IconButton size="small"
                        onMouseDown={(e) => { e.preventDefault(); setTableAnchor(e.currentTarget); }}
                        sx={{ p: 0.5, borderRadius: 1,
                              color: inTable ? "#2563eb" : "#475569",
                              bgcolor: inTable ? "#dbeafe" : "transparent",
                              "&:hover": { bgcolor: inTable ? "#dbeafe" : "#f1f5f9" } }}>
                        <TableRowsRoundedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
                <TablePopover
                    anchorEl={tableAnchor}
                    open={Boolean(tableAnchor)}
                    onClose={() => setTableAnchor(null)}
                    inTable={inTable}
                    onInsert={(r, c) => { handleInsertTable(r, c); setTableAnchor(null); }}
                    editActions={tableEditActions}
                    onCellBackground={(color) => {
                        // setCellAttribute 는 현재 NodeSelection/CellSelection 의 모든 셀에 동일 attribute 적용.
                        // color === null 이면 (지우기 버튼) 배경 해제 — renderHTML 이 빈 객체 반환.
                        editor.chain().focus().setCellAttribute("backgroundColor", color).run();
                    }}
                />

                <Sep />

                <Tooltip title="이미지 삽입" placement="top">
                    <IconButton size="small"
                        onMouseDown={(e) => { e.preventDefault(); imageInputRef.current?.click(); }}
                        sx={{ p: 0.5, borderRadius: 1, color: "#475569", "&:hover": { bgcolor: "#f1f5f9" } }}>
                        <AddPhotoAlternateRoundedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
                <input
                    ref={imageInputRef}
                    type="file" accept="image/*" hidden multiple
                    onChange={async e => {
                        const files = Array.from(e.target.files || []);
                        if (!files.length) return;
                        for (const file of files) {
                            try {
                                let src = null;
                                if (onUploadImage) src = await onUploadImage(file);
                                if (!src) src = await readFileAsDataUrl(file);
                                if (src) editor.chain().focus().setImage({ src }).run();
                            } catch (err) {
                                console.error("이미지 처리 실패", err);
                                notify(`${file.name} 이미지 처리 실패`, "error");
                            }
                        }
                        e.target.value = "";
                    }}
                />

                <Box sx={{ flex: 1 }} />

                <ToolBtn title="실행 취소" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><UndoRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>
                <ToolBtn title="다시 실행" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><RedoRoundedIcon sx={{ fontSize: 17 }} /></ToolBtn>
            </Box>

            {/* ── 본문 ──────────────────────────────────── */}
            <Box sx={{
                flex: 1, overflowY: "auto",
                "& .rich-editor-content": {
                    minHeight: minHeight, padding: "16px 18px",
                    fontFamily: MALGUN_GOTHIC,
                    fontSize: 14, lineHeight: 1.55, color: "#0f172a",
                    outline: "none",
                },
                /* 단락 사이 큰 빈 줄 문제 — Tiptap 기본 p margin 을 컴팩트하게.
                   사용자가 엔터 한 번 = 한 줄 간격이 자연스러워지도록. */
                "& .rich-editor-content p":      { margin: "0 0 4px" },
                "& .rich-editor-content h1, & .rich-editor-content h2, & .rich-editor-content h3, & .rich-editor-content h4, & .rich-editor-content h5, & .rich-editor-content h6":
                    { margin: "10px 0 6px" },
                "& .rich-editor-content ul, & .rich-editor-content ol": { margin: "4px 0", paddingLeft: "1.5em" },
                "& .rich-editor-content li":     { margin: "0 0 2px" },
                "& .rich-editor-content > *:first-child": { marginTop: 0 },
                "& .rich-editor-content > *:last-child":  { marginBottom: 0 },
                "& .rich-editor-content p.is-editor-empty:first-child::before": {
                    content: `"${placeholder.replace(/"/g, '\\"')}"`,
                    color: "#94a3b8", float: "left", height: 0, pointerEvents: "none",
                },
                "& .rich-editor-content a":   { color: "#2563eb", textDecoration: "underline" },
                "& .rich-editor-content table": {
                    borderCollapse: "collapse", margin: "8px 0", width: "100%",
                    tableLayout: "fixed", // resizable: true 일 때 col 폭 변경이 즉시 반영되도록 fixed
                    "& td, & th": {
                        border: "1px solid #e2e8f0", padding: "6px 8px",
                        position: "relative",   // column-resize-handle 의 absolute 기준
                        verticalAlign: "top",
                        boxSizing: "border-box",
                    },
                    "& th": { bgcolor: "#f8fafc", fontWeight: 600 },
                    "& .selectedCell:after": {
                        content: '""', position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(37, 99, 235, 0.10)",
                        pointerEvents: "none", zIndex: 2,
                    },
                },
                /*
                  컬럼 리사이즈 핸들 — Tiptap @tiptap/extension-table 의 resizable: true 가
                  렌더하는 div.column-resize-handle 에 시각 스타일을 부여해야 사용자가 잡을 수 있음.
                  기본 상태로는 핸들이 보이지 않아 "리사이즈가 안 된다" 처럼 느껴짐.
                */
                "& .rich-editor-content .column-resize-handle": {
                    position: "absolute",
                    right: "-2px", top: 0, bottom: "-1px",
                    width: "4px",
                    backgroundColor: "#60a5fa",
                    cursor: "col-resize",
                    pointerEvents: "none", // ProseMirror 가 핸들 자체에 이벤트 매니지먼트
                    zIndex: 3,
                },
                "& .rich-editor-content .tableWrapper": {
                    overflowX: "auto", margin: "8px 0",
                },
                /*
                  서명 블록 안의 표는 예외 — 편집 대상이 아니라 완성된 레이아웃이다.

                  명함형 서명은 표를 "그리드"가 아니라 2단 레이아웃 도구로 쓴다.
                  (좌: 이름·직급 / 우: 로고, 아래에 빨간 밑줄 한 줄). 테두리는 일부러 없고,
                  td 인라인 style 의 border-bottom 하나만 살아 있어야 하는 구조.
                  여기에 위 표 규칙(모든 td 에 border + width:100% + table-layout:fixed)이 걸리면
                  격자무늬가 생기고 폭이 늘어나 디자인이 통째로 부서진다.

                  선택자 구체도가 위 규칙보다 높아([data-signature] 속성 1개만큼) 덮어쓴다.
                  border:none / padding:0 은 "기본값으로 되돌리기" 일 뿐 —
                  서명이 실제로 가진 인라인 style 은 인라인이라 그대로 이긴다.
                */
                "& .rich-editor-content [data-signature] table": {
                    width: "auto", tableLayout: "auto", margin: 0,
                    "& td, & th": { border: "none", padding: 0 },
                },
                "& .rich-editor-content .resize-cursor": {
                    cursor: "col-resize",
                },
                /* 이미지 클릭/포커스 시 시각 피드백 — 더블클릭으로 크기 조절 가능함을 알림 */
                "& .rich-editor-content img": {
                    maxWidth: "100%", height: "auto", borderRadius: 4,
                    cursor: "pointer",
                },
                "& .rich-editor-content img.ProseMirror-selectednode": {
                    outline: "2px solid #2563eb",
                    outlineOffset: "2px",
                },
                "& .rich-editor-content hr": { border: "none", borderTop: "1px solid #e2e8f0", margin: "10px 0" },
                "& .rich-editor-content blockquote": {
                    borderLeft: "3px solid #cbd5e1", margin: "6px 0",
                    paddingLeft: "12px", color: "#64748b",
                },
            }}>
                <EditorContent editor={editor} />
            </Box>

            {/* @ 멘션 자동완성 — 채널/메일 본문 통합 컴포넌트. mentionMembers 또는 mentionFetcher 가
                있을 때만 활성. 비어있으면 컴포넌트 내부에서 noop. */}
            <MentionAutocomplete
                editor={editor}
                members={mentionMembers}
                fetcher={mentionFetcher}
                onPicked={onMentionPicked}
            />

            {/* 이미지 크기 조절 다이얼로그 — 본문 이미지 더블클릭 시 열림 */}
            <ImageResizeDialog
                state={imgResize}
                onClose={() => setImgResize(null)}
                onApply={({ width, height }) => {
                    // ProseMirror — 같은 src 의 이미지 노드 attrs 를 갱신.
                    // setImage 는 새 노드를 삽입하므로 기존 노드 위치 보존을 위해 updateAttributes 사용.
                    editor.chain().focus().updateAttributes("image", { width, height: height || null }).run();
                    setImgResize(null);
                }}
            />
        </Box>
    );
}

/* ──────────────────────────────────────────────────────────────────────────
 *  TablePopover — "표" 툴바 버튼의 드롭다운.
 *
 *  구조:
 *   ┌──────────────────────┐
 *   │  N × M  표 삽입       │   ← hover 라벨
 *   │  ┌─┬─┬─┬─┬─┬─┬─┬─┐    │
 *   │  ├─┼─┼─┼─┼─┼─┼─┼─┤    │   ← 10×10 그리드. 마우스 hover 시
 *   │  ├─┼─┼─┼─┼─┼─┼─┼─┤    │     좌상단~현재 셀까지 파랑 채움.
 *   │  └─┴─┴─┴─┴─┴─┴─┴─┘    │   ← 클릭 → onInsert(rows, cols)
 *   ├──────────────────────┤
 *   │  (편집 액션 — 표 안에 있을 때만 enabled)
 *   │   위에 행 추가
 *   │   아래에 행 추가
 *   │   ...                  │
 *   └──────────────────────┘
 *
 *  Tiptap 표 명령은 onMouseDown 으로 트리거 — Popover 가 외부 클릭으로 닫히기 전에
 *  명령이 먼저 실행되도록 (click 보다 빠른 phase). 안 그러면 editor focus 가 잠시
 *  Popover 로 옮겨가 명령이 caret 위치를 잃음.
 *  ────────────────────────────────────────────────────────────────────── */
const GRID_ROWS = 10;
const GRID_COLS = 10;

/** 표 셀 배경색 팔레트 — 메일 본문에 어울리는 부드러운 파스텔 톤 + 표준 강조색 + 투명. */
const CELL_BG_PALETTE = [
    null,         // 지우기 (transparent)
    "#fef9c3",    // 연노랑
    "#fed7aa",    // 연주황
    "#fecaca",    // 연빨강
    "#fbcfe8",    // 연분홍
    "#e9d5ff",    // 연보라
    "#bfdbfe",    // 연파랑
    "#bbf7d0",    // 연초록
    "#fde68a",    // 노랑
    "#cffafe",    // 연하늘
    "#f1f5f9",    // 연회색 (헤더 톤)
    "#e2e8f0",    // 회색
];

function TablePopover({ anchorEl, open, onClose, inTable, onInsert, editActions, onCellBackground }) {
    const [hover, setHover] = useState({ r: 0, c: 0 });

    useEffect(() => { if (open) setHover({ r: 0, c: 0 }); }, [open]);

    return (
        <Popover
            open={open} anchorEl={anchorEl} onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ sx: { p: 1.2, borderRadius: 1.5, minWidth: 220 } }}>

            <Typography sx={{
                fontSize: 12, fontWeight: 700, color: "#0f172a", mb: 0.6, ml: 0.2,
            }}>
                {hover.r > 0 && hover.c > 0 ? `${hover.r} × ${hover.c}` : "표 크기 선택"}
            </Typography>
            <Box sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${GRID_COLS}, 16px)`,
                gridTemplateRows:    `repeat(${GRID_ROWS}, 16px)`,
                gap: "2px",
                mb: 0.8,
            }}>
                {Array.from({ length: GRID_ROWS }).map((_, ri) =>
                    Array.from({ length: GRID_COLS }).map((_, ci) => {
                        const r = ri + 1, c = ci + 1;
                        const active = r <= hover.r && c <= hover.c;
                        return (
                            <Box
                                key={`${r}-${c}`}
                                onMouseEnter={() => setHover({ r, c })}
                                onMouseDown={(e) => { e.preventDefault(); onInsert(r, c); }}
                                sx={{
                                    width: 16, height: 16, borderRadius: 0.4,
                                    cursor: "pointer",
                                    bgcolor: active ? "#2563eb" : "#fff",
                                    border: "1px solid",
                                    borderColor: active ? "#1d4ed8" : "#e2e8f0",
                                    transition: "background-color 0.04s",
                                }}
                            />
                        );
                    })
                )}
            </Box>

            <Divider sx={{ my: 0.6 }} />

            {/* 셀 배경색 — 표 안에서만 활성. 현재 선택된 셀(들)의 backgroundColor attribute 변경. */}
            <Typography sx={{
                fontSize: 12, fontWeight: 700, color: inTable ? "#0f172a" : "#cbd5e1",
                mb: 0.5, ml: 0.2,
            }}>
                셀 배경색
            </Typography>
            <Box sx={{
                display: "grid", gridTemplateColumns: "repeat(6, 22px)", gap: "4px",
                mb: 0.6, opacity: inTable ? 1 : 0.4,
            }}>
                {CELL_BG_PALETTE.map((color, i) => {
                    const isClear = color === null;
                    return (
                        <Box key={i}
                            onMouseDown={(e) => {
                                if (!inTable) return;
                                e.preventDefault();
                                onCellBackground(color);
                                onClose();
                            }}
                            sx={{
                                width: 22, height: 22, borderRadius: 0.5,
                                cursor: inTable ? "pointer" : "default",
                                bgcolor: isClear ? "#fff" : color,
                                // "지우기" 칸은 빨간 사선 표시 — 색상 제거임을 명시
                                backgroundImage: isClear
                                    ? "linear-gradient(135deg, transparent 47%, #ef4444 47%, #ef4444 53%, transparent 53%)"
                                    : "none",
                                border: "1px solid #e2e8f0",
                                "&:hover": inTable ? { transform: "scale(1.15)" } : {},
                                transition: "transform 0.1s",
                            }}
                            title={isClear ? "배경색 지우기" : color}
                        />
                    );
                })}
            </Box>

            <Divider sx={{ my: 0.6 }} />

            {/* 편집 액션 — 표 안에서만 활성. 외부에선 회색으로 보여 정보만 제공 */}
            {editActions.map(item => item.divider
                ? <Divider key={item.key} sx={{ my: 0.4 }} />
                : (
                    <Box key={item.key}
                        onMouseDown={(e) => {
                            if (!inTable) return;
                            e.preventDefault();
                            item.run();
                            onClose();
                        }}
                        sx={{
                            px: 1, py: 0.7, borderRadius: 0.8,
                            fontSize: 12.5,
                            color: !inTable ? "#cbd5e1" : (item.danger ? "#dc2626" : "#0f172a"),
                            cursor: inTable ? "pointer" : "default",
                            "&:hover": inTable ? {
                                bgcolor: item.danger ? "#fee2e2" : "#f1f5f9",
                            } : {},
                        }}>
                        {item.label}
                    </Box>
                )
            )}
        </Popover>
    );
}

/* ──────────────────────────────────────────────────────────────────────────
 *  ImageResizeDialog — 본문 이미지 더블클릭 시 열리는 크기 조절 모달.
 *
 *  - 미리보기: 현재 src 를 width 입력값에 맞춰 실시간 스케일
 *  - 너비 입력 → 비율 유지 모드면 height 자동 갱신
 *  - 높이 입력 → 비율 유지 모드면 width 자동 갱신
 *  - "원본 크기" 버튼: naturalW/H 로 즉시 복원
 *  - "원본 크기 유지" 체크박스: 적용 시 width/height 모두 null 로 → renderHTML 이 출력 안 함
 *    → 받는 사람도 원본 크기 그대로 보임 (max-width:100% 만 적용)
 *  ────────────────────────────────────────────────────────────────────── */
function ImageResizeDialog({ state, onClose, onApply }) {
    const [width, setWidth]         = useState(0);
    const [height, setHeight]       = useState(0);
    const [lockRatio, setLockRatio] = useState(true);
    const [keepOriginal, setKeepOriginal] = useState(false);

    useEffect(() => {
        if (!state) return;
        setWidth(state.width || state.naturalW || 600);
        setHeight(state.height || (state.naturalW && state.naturalH
            ? Math.round(state.naturalH * ((state.width || state.naturalW) / state.naturalW))
            : 0));
        setLockRatio(true);
        setKeepOriginal(false);
    }, [state]);

    if (!state) return null;

    const ratio = state.naturalW > 0 && state.naturalH > 0
        ? state.naturalH / state.naturalW
        : null;

    const onWidthChange = (v) => {
        const n = Math.max(0, parseInt(v, 10) || 0);
        setWidth(n);
        if (lockRatio && ratio) setHeight(Math.round(n * ratio));
    };
    const onHeightChange = (v) => {
        const n = Math.max(0, parseInt(v, 10) || 0);
        setHeight(n);
        if (lockRatio && ratio) setWidth(Math.round(n / ratio));
    };
    const onResetOriginal = () => {
        if (state.naturalW) setWidth(state.naturalW);
        if (state.naturalH) setHeight(state.naturalH);
    };

    return (
        <Dialog open={Boolean(state)} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 1.5 } }}>
            <DialogTitle sx={{ pb: 1, fontSize: 15, fontWeight: 700 }}>이미지 보정</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", mb: 0.6 }}>
                    미리 보기
                </Typography>
                <Box sx={{
                    border: "1px solid #e2e8f0", borderRadius: 1,
                    bgcolor: "#fafbfc", display: "flex", justifyContent: "center",
                    alignItems: "center", p: 1, minHeight: 180, mb: 2,
                }}>
                    <img
                        src={state.src} alt=""
                        style={{
                            maxWidth: "100%", maxHeight: 280,
                            width:  width  > 0 && !keepOriginal ? `${width}px`  : "auto",
                            height: height > 0 && !keepOriginal ? `${height}px` : "auto",
                        }}
                    />
                </Box>

                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", mb: 0.6 }}>
                    크기 (px)
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <TextField label="너비" size="small" type="number"
                        value={width} disabled={keepOriginal}
                        onChange={(e) => onWidthChange(e.target.value)}
                        sx={{ width: 120 }} InputProps={{ inputProps: { min: 0, max: 4000 } }} />
                    <TextField label="높이" size="small" type="number"
                        value={height} disabled={keepOriginal}
                        onChange={(e) => onHeightChange(e.target.value)}
                        sx={{ width: 120 }} InputProps={{ inputProps: { min: 0, max: 4000 } }} />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={lockRatio}
                            disabled={keepOriginal}
                            onChange={(e) => setLockRatio(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 12.5 }}>너비/높이 비율 유지</Typography>}
                        sx={{ ml: 0 }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" variant="outlined" onClick={onResetOriginal}
                        disabled={keepOriginal || !state.naturalW}
                        sx={{ textTransform: "none", fontSize: 12 }}>
                        원래 크기
                    </Button>
                </Box>

                <FormControlLabel
                    control={<Checkbox size="small" checked={keepOriginal}
                        onChange={(e) => setKeepOriginal(e.target.checked)} />}
                    label={<Typography sx={{ fontSize: 12.5 }}>원본 크기 유지 (받는 쪽도 원본 그대로)</Typography>}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <ActionButton variant="cancel" onClick={onClose} />
                <ActionButton variant="primary"
                    onClick={() => onApply(keepOriginal
                        ? { width: null, height: null }
                        : { width: width || null, height: lockRatio ? null : (height || null) })}
                    label="이미지로 삽입" />
            </DialogActions>
        </Dialog>
    );
}
